import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FileProvider, useFileContext } from "context/FilesV2";
import {
  formatFiles,
  downloadFile,
  uploadFiles,
  deleteFile,
  deleteBundle,
} from "util";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FileGroup } from "common/FileUpload";
import { getFiles } from "apis/controllers/files";
import { getSchoolClasses, getSchools } from "apis/controllers/applications";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { Attachment, Sync } from "@mui/icons-material";
import { generateAttachments } from "apis/controllers/externalData";
import { createMarkup } from "util/createMarkup";
import { useSelector } from "react-redux";
import { CssTextField } from "qubCommon/Inputs/CssTextField";
import ColumnLabel from "qubCommon/Inputs/ColumnLabel";
import ClientAutocomplete from "qubCommon/Inputs/ClientAutocomplete";

const STUDENT_PLUS_18_CATEGORY_ID = 7;
const SCHOOL_SELECTION_SEASON_ID = 2026;

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const toOptionalNumber = (value) => (hasValue(value) ? Number(value) : "");

const getLookupId = (lookup) => lookup?.id ?? "";

const normalizeLookup = (lookup) => (hasValue(lookup?.id) ? lookup : null);

const findLookupById = (options, id) =>
  (options || []).find((option) => String(option?.id) === String(id)) || null;

const withSelectedOption = (options, selectedOption) => {
  if (!hasValue(selectedOption?.id)) return options || [];
  const selectedExists = (options || []).some(
    (option) => String(option?.id) === String(selectedOption.id)
  );
  return selectedExists ? options || [] : [selectedOption, ...(options || [])];
};

const getInitialSchoolSelection = (child, defaultSchoolGeo) => ({
  schoolGeo: normalizeLookup(child?.schoolGeo) || normalizeLookup(defaultSchoolGeo),
  schoolType: normalizeLookup(child?.schoolType),
  school: normalizeLookup(child?.school),
  schoolClass: normalizeLookup(child?.schoolClass),
  schoolRegNumber: child?.schoolRegNumber ?? "",
});

const getSchoolSelectionPayload = (selection) => ({
  schoolGeo: normalizeLookup(selection?.schoolGeo),
  schoolType: normalizeLookup(selection?.schoolType),
  school: normalizeLookup(selection?.school),
  schoolClass: normalizeLookup(selection?.schoolClass),
  schoolRegNumber: hasValue(selection?.schoolRegNumber)
    ? String(selection.schoolRegNumber).trim()
    : "",
});

const ChildSchoolField = ({
  label,
  variant,
  select = false,
  children,
  selectProps,
  ...props
}) => (
  <Stack spacing={1}>
    {variant === "standard" && <ColumnLabel label={label} />}
    <CssTextField
      select={select}
      fullWidth
      size="small"
      label={variant === "standard" ? null : label}
      variant={variant}
      InputProps={{
        disableUnderline: true,
      }}
      SelectProps={
        select
          ? {
            displayEmpty: true,
            MenuProps: {
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "center",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            },
            ...selectProps,
          }
          : undefined
      }
      fixedPadding={8}
      fixedPaddingLeft={12}
      {...props}
    >
      {children}
    </CssTextField>
  </Stack>
);

const ChildMunicipalityAutocomplete = ({
  name,
  label,
  options,
  value,
  disabled,
  onChange,
}) => {
  const { setValue } = useFormContext();
  const selectedId = getLookupId(value);

  useEffect(() => {
    setValue(name, selectedId || null, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [name, selectedId, setValue]);

  return (
    <ClientAutocomplete
      name={name}
      label={label}
      valueName="id"
      labelName="name"
      options={options}
      defaultValue={selectedId || null}
      customChangeFn={onChange}
      disabled={disabled}
    />
  );
};

const ChildSchoolSelector = ({
  value,
  onChange,
  disabled = false,
  lookups = {},
  applicationId,
  seasonId,
  municipalityFieldName,
}) => {
  const { fieldsVariant } = useSelector((state) => state.customization);
  const variant = fieldsVariant ?? "standard";
  const municipalityOptions = withSelectedOption(
    lookups?.municipalities || [],
    value.schoolGeo
  );
  const schoolTypeOptions = withSelectedOption(
    lookups?.schoolTypes || [],
    value.schoolType
  );
  const schoolGeoId = getLookupId(value.schoolGeo);
  const schoolTypeId = getLookupId(value.schoolType);
  const schoolId = getLookupId(value.school);
  const schoolClassId = getLookupId(value.schoolClass);

  const schoolsQuery = useQuery(
    ["schools", applicationId, seasonId, schoolGeoId, schoolTypeId],
    async () => {
      const res = await getSchools({
        geoLocationId: schoolGeoId,
        schoolType: schoolTypeId,
        ...(applicationId ? { applicationId } : {}),
        ...(!applicationId && hasValue(seasonId) ? { seasonId } : {}),
      });
      return res?.data || [];
    },
    {
      enabled: hasValue(schoolGeoId) && hasValue(schoolTypeId),
      keepPreviousData: true,
    }
  );
  const schoolOptions = withSelectedOption(
    schoolsQuery.data || [],
    value.school
  );

  const schoolClassesQuery = useQuery(
    ["school-classes", applicationId, seasonId, schoolId],
    async () => {
      const res = await getSchoolClasses({
        schoolId,
        ...(applicationId ? { applicationId } : {}),
        ...(!applicationId && hasValue(seasonId) ? { seasonId } : {}),
      });
      return res?.data || [];
    },
    {
      enabled: hasValue(schoolId),
      keepPreviousData: false,
    }
  );
  const schoolClassOptions = withSelectedOption(
    schoolClassesQuery.data || [],
    value.schoolClass
  );

  const handleMunicipalityChange = (schoolGeo) => {
    onChange({
      ...value,
      schoolGeo: normalizeLookup(schoolGeo),
      school: null,
      schoolClass: null,
    });
  };

  const handleSchoolTypeChange = (event) => {
    const schoolType = findLookupById(
      schoolTypeOptions,
      toOptionalNumber(event.target.value)
    );
    onChange({ ...value, schoolType, school: null, schoolClass: null });
  };

  const handleSchoolChange = (event) => {
    const school = findLookupById(
      schoolOptions,
      toOptionalNumber(event.target.value)
    );
    onChange({ ...value, school, schoolClass: null });
  };

  const handleSchoolClassChange = (event) => {
    const schoolClass = findLookupById(
      schoolClassOptions,
      toOptionalNumber(event.target.value)
    );
    onChange({ ...value, schoolClass });
  };

  const handleSchoolRegNumberChange = (event) => {
    onChange({ ...value, schoolRegNumber: event.target.value });
  };

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <Typography variant='h4' sx={{ fontWeight: 700 }}>
          Στοιχεία σχολείου
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChildMunicipalityAutocomplete
          name={municipalityFieldName}
          label="Δήμος"
          options={municipalityOptions}
          value={value.schoolGeo}
          disabled={disabled}
          onChange={handleMunicipalityChange}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ChildSchoolField
          select
          label="Τύπος Σχολείου"
          variant={variant}
          value={schoolTypeId}
          disabled={disabled || !hasValue(schoolGeoId)}
          onChange={handleSchoolTypeChange}
        >
          <MenuItem value="">Επιλέξτε</MenuItem>
          {schoolTypeOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </ChildSchoolField>
      </Grid>
      <Grid item xs={12} md={3}>
        <Stack direction="column" gap={1}>
          <ColumnLabel label="Σχολικό Έτος" />

          <CssTextField
            variant='standard'
            InputProps={{
              disableUnderline: true,
            }}
            fixedPadding={8}
            value={seasonId ? `${String(seasonId - 1)} - ${String(seasonId)}` : ""}
            disabled
          />
        </Stack>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChildSchoolField
          select
          label="Σχολείο"
          variant={variant}
          value={getLookupId(value.school)}
          disabled={
            disabled ||
            !hasValue(schoolGeoId) ||
            !hasValue(schoolTypeId) ||
            schoolsQuery.isLoading
          }
          onChange={handleSchoolChange}
        >
          <MenuItem value="">Επιλέξτε</MenuItem>
          {schoolsQuery.isLoading && (
            <MenuItem value="" disabled>
              Φόρτωση...
            </MenuItem>
          )}
          {schoolsQuery.isError && (
            <MenuItem value="" disabled>
              Αδυναμία φόρτωσης σχολείων
            </MenuItem>
          )}
          {!schoolsQuery.isLoading &&
            !schoolsQuery.isError &&
            hasValue(schoolGeoId) &&
            hasValue(schoolTypeId) &&
            schoolOptions.length === 0 && (
              <MenuItem value="" disabled>
                Δεν βρέθηκαν σχολεία
              </MenuItem>
            )}
          {schoolOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </ChildSchoolField>
      </Grid>
      <Grid item xs={12} md={3}>
        <ChildSchoolField
          select
          label="Τάξη"
          variant={variant}
          value={schoolClassId}
          disabled={disabled || !hasValue(schoolId) || schoolClassesQuery.isLoading}
          onChange={handleSchoolClassChange}
        >
          <MenuItem value="">Επιλέξτε</MenuItem>
          {schoolClassesQuery.isLoading && (
            <MenuItem value="" disabled>
              Φόρτωση...
            </MenuItem>
          )}
          {schoolClassesQuery.isError && (
            <MenuItem value="" disabled>
              Αδυναμία φόρτωσης τάξεων
            </MenuItem>
          )}
          {!schoolClassesQuery.isLoading &&
            !schoolClassesQuery.isError &&
            hasValue(schoolId) &&
            schoolClassOptions.length === 0 && (
              <MenuItem value="" disabled>
                Δεν βρέθηκαν τάξεις
              </MenuItem>
            )}
          {schoolClassOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </ChildSchoolField>
      </Grid>
      <Grid item xs={12} md={3}>
        <ChildSchoolField
          label="Α.Μ. Μαθητή"
          variant={variant}
          value={value.schoolRegNumber ?? ""}
          disabled={disabled}
          onChange={handleSchoolRegNumberChange}
        />
      </Grid>
    </Grid>
  );
};

const FilesUpload = forwardRef(
  ({ fileGroups, childId, settings = {}, schoolSelection }, ref) => {
    const { fileGroups: fileGroupsV2, control: filesControl } =
      useFileContext();

    useImperativeHandle(ref, () => ({
      uploadFiles: async () => {
        await filesControl.uploadFileGroups();
      },
      filebundles: {
        files: fileGroupsV2?.flatMap((group) => group?.bundles),
        childId,
        hasSchoolSelectionPayload: Boolean(schoolSelection),
        ...(schoolSelection || {}),
        // personFunctionId: id,
        // functionId: functionId,
      },
    }));

    useEffect(() => {
      try {
        const formattedFiles = formatFiles(fileGroups);
        if (formattedFiles !== undefined) {
          filesControl.initialize(formattedFiles);
        } else {
          filesControl.initialize([]);
        }
      } catch (error) {
        console.error("error", error);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileGroups]);
    return (
      <>
        {fileGroupsV2?.map((group) => (
          <FileGroup
            reviewMode={settings?.canApprove}
            isReadOnly={!settings?.canEdit}
            canAdd={settings?.canAdd}
            key={group.id}
            id={group.id}
            title={group?.title}
            maxSize={5242880}
            maxFiles={5}
            maxBundles={1}
            fields={group.fields}
            bundles={group?.bundles}
            templates={group?.templates}
            canViewReview={settings?.canViewReview}
          />
        ))}
      </>
    );
  }
);

const ChildAttachments = (
  {
    child,
    dataKey,
    dataValue,
    season,
    lookups = {},
    applicantMunicipality,
    formIndex,
    showSchoolSelector,
    canEditSchool,
    settings = {},
  },
  ref
) => {

  const { t } = useTranslation();
  const { id } = useParams();
  const categoryId = child?.category?.id;
  const shouldShowButton = (settings?.wsBtns || []).some(
    btn => btn.id === categoryId && btn.enabled
  );
  const shouldShowSchoolSelector =
    typeof showSchoolSelector === "boolean"
      ? showSchoolSelector
      : Number(season?.id) === SCHOOL_SELECTION_SEASON_ID &&
      Number(categoryId) === STUDENT_PLUS_18_CATEGORY_ID;
  const applicationId = id && id !== "0" ? id : undefined;
  const childSelectionFormKey = child?.id ?? formIndex;
  const municipalityFieldName =
    `childSchoolSelections.${childSelectionFormKey}.schoolGeoId`;


  const [loadingWs, setLoadingWs] = useState(false);
  const [alert, setAlert] = useState({ severity: "", message: "" })
  const [schoolSelection, setSchoolSelection] = useState(() =>
    getInitialSchoolSelection(child, applicantMunicipality)
  );

  useEffect(() => {
    setSchoolSelection(getInitialSchoolSelection(child, applicantMunicipality));
  }, [
    child?.id,
    child?.schoolGeo?.id,
    child?.schoolType?.id,
    child?.school?.id,
    child?.schoolClass?.id,
    child?.schoolRegNumber,
  ]);

  useEffect(() => {
    if (!hasValue(child?.schoolGeo?.id) && hasValue(applicantMunicipality?.id)) {
      setSchoolSelection((current) =>
        hasValue(current?.schoolGeo?.id)
          ? current
          : { ...current, schoolGeo: normalizeLookup(applicantMunicipality) }
      );
    }
  }, [
    child?.schoolGeo?.id,
    applicantMunicipality?.id,
  ]);

  const filesQuery = useQuery(
    ["files", child?.id, dataValue, dataKey],
    async ({ queryKey }) => {
      const [, , dataValue] = queryKey;


      const getFilesRequest = {
        DataKey: dataKey,
        DataValue: dataValue,
        RecordId: null,
        RecordGuid: child?.id,
        ProjectTableId: 7,
        applyRules: true,
      };
      const files = await getFiles(getFilesRequest);
      return files?.data;
    }
  );

  const handleWs = async () => {
    setLoadingWs(true)
    try {
      const res = await generateAttachments(id, child?.id)
      setAlert({ severity: res?.status, message: res?.message })
    }
    catch {
      setAlert({ severity: 'error', message: 'Παρουσιάστηκε σφάλμα κατά την επικοινωνία με την υπηρεσία. Παρακαλούμε δοκιμάστε ξανά αργότερα.' })
    }
    finally {
      filesQuery.refetch()
      setLoadingWs(false)
    }
  }

  const AccordionTitle = () => (
    <Stack direction="row" gap={1}>
      <Attachment color="primary" fontSize="small" sx={{ marginLeft: "2px" }} />

      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΔΙΚΑΙΟΛΟΓΗΤΙΚΑ ΤΕΚΝΟΥ - {child?.lastName} {child?.firstName}
      </Typography>
      {/* <Typography color="primary" sx={{ marginLeft: "18px", fontWeight: 500 }}>
          {institute?.abbr}
        </Typography> */}
    </Stack>
  );

  return {
    loading: <Skeleton />,
    success:
      filesQuery?.data?.length === 0 && !shouldShowSchoolSelector ? null : (
        <CustomSimpleAccordion
          title={<AccordionTitle />}
          content={
            <FileProvider
              downloadFile={(fileId, type) => downloadFile(fileId, type)}
              uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
              onDeleteBundle={async (bundleId) =>
                await deleteBundle(bundleId, 7, null, child?.id, null)
              }
              onDeleteFile={async (fileId) =>
                await deleteFile(fileId, 7, null, id === "0" ? null : id, null)
              }
            >
              {shouldShowSchoolSelector && (
                <ChildSchoolSelector
                  value={schoolSelection}
                  onChange={setSchoolSelection}
                  disabled={!(canEditSchool ?? settings?.canEdit)}
                  lookups={lookups}
                  applicationId={applicationId}
                  seasonId={season?.id}
                  municipalityFieldName={municipalityFieldName}
                />
              )}
              {shouldShowButton && (
                <Button
                  sx={{ mb: 1 }}
                  variant="contained"
                  disabled={(filesQuery.isFetching || loadingWs)}
                  startIcon={
                    (filesQuery.isFetching || loadingWs) ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <Sync />
                    )
                  }
                  onClick={handleWs}
                >
                  WebService - Έλεγχος
                </Button>
              )}
              <FilesUpload
                fileGroups={filesQuery?.data}
                ref={ref}
                childId={child?.id}
                schoolSelection={
                  shouldShowSchoolSelector
                    ? getSchoolSelectionPayload(schoolSelection)
                    : undefined
                }
                settings={settings}
              />
              <Dialog
                open={Boolean(alert?.severity && alert?.message)}
                onClose={() => setAlert({ severity: "", message: "" })}
              >
                <DialogTitle variant='h4'>
                  {alert?.severity === "success"
                    ? t("Επιτυχία")
                    : alert?.severity === "error"
                      ? t("Σφάλμα")
                      : t("Ενημέρωση")}
                </DialogTitle>
                <DialogContent dividers>
                  <div dangerouslySetInnerHTML={createMarkup(alert?.message)} />
                </DialogContent>
                <DialogActions>
                  <Button variant='contained' onClick={() => setAlert({ severity: "", message: "" })}>
                    OK
                  </Button>
                </DialogActions>
              </Dialog>
            </FileProvider>
          }
        />
      ),
    error: <>Error fetching required attachments</>,
  }[filesQuery?.status];
};

export default forwardRef(ChildAttachments);
